import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react'
import { notificationsService, type Notification } from '../services/notifications'
import { DateDisplay } from './ui/DateDisplay'
import { toast } from 'sonner'

const NOTIFICATION_ICONS: Record<string, string> = {
  task_assigned: '📋',
  mention: '💬',
  deadline_tomorrow: '⏰',
  milestone_approaching: '🏁',
  change_request_pending: '📝',
  ai_insight_critical: '🤖',
  governance_overdue: '⚠',
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await notificationsService.unreadCount()
      setUnreadCount(count)
    } catch {
      // fail silently for count polling
    }
  }, [])

  useEffect(() => {
    loadUnreadCount()
    const interval = setInterval(loadUnreadCount, 30_000)
    return () => clearInterval(interval)
  }, [loadUnreadCount])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleOpen = async () => {
    setOpen(v => !v)
    if (!open) {
      setLoading(true)
      try {
        const items = await notificationsService.list(false, 50)
        setNotifications(items)
      } catch {
        toast.error('Failed to load notifications')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await notificationsService.markRead()
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch {
      toast.error('Failed to mark notifications as read')
    }
  }

  const handleMarkOne = async (id: number) => {
    try {
      await notificationsService.markRead([id])
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch {
      toast.error('Failed to mark notification as read')
    }
  }

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await notificationsService.delete(id)
      setNotifications(prev => prev.filter(n => n.id !== id))
      const deleted = notifications.find(n => n.id === id)
      if (deleted && !deleted.read) setUnreadCount(prev => Math.max(0, prev - 1))
    } catch {
      toast.error('Failed to delete notification')
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleOpen}
        className="relative p-2.5 rounded-lg hover:bg-[#3D4F61] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#DAA520]"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="w-5 h-5 text-[#C5CDD6]" aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none"
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 flex flex-col max-h-[560px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
              <a
                href="/settings/notifications"
                className="text-xs text-[#486581] hover:underline"
              >
                Settings
              </a>
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-[#486581] hover:bg-gray-100 rounded-md transition-colors min-h-[32px]"
                  aria-label="Mark all as read"
                >
                  <CheckCheck className="w-3.5 h-3.5" aria-hidden="true" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                aria-label="Close notifications"
              >
                <X className="w-4 h-4 text-gray-500" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* List */}
          <ul className="flex-1 overflow-y-auto divide-y divide-gray-50" role="list">
            {loading ? (
              <li className="flex items-center justify-center py-8 text-sm text-gray-500">Loading…</li>
            ) : notifications.length === 0 ? (
              <li className="flex flex-col items-center justify-center py-10 text-sm text-gray-500">
                <Bell className="w-8 h-8 text-gray-300 mb-2" aria-hidden="true" />
                No notifications
              </li>
            ) : (
              notifications.map(n => (
                <li
                  key={n.id}
                  className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    n.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/60 hover:bg-blue-50'
                  }`}
                  onClick={() => {
                    if (!n.read) handleMarkOne(n.id)
                    if (n.link) window.location.href = n.link
                  }}
                  role="listitem"
                >
                  <span className="text-lg flex-shrink-0 mt-0.5" aria-hidden="true">
                    {NOTIFICATION_ICONS[n.type] ?? '🔔'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.read ? 'text-gray-700' : 'text-gray-900 font-medium'} leading-snug`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <DateDisplay date={n.created_at} className="mt-1 text-[11px] text-gray-400" />
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    {!n.read && (
                      <button
                        onClick={e => { e.stopPropagation(); handleMarkOne(n.id) }}
                        className="p-1 hover:bg-white rounded transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
                        aria-label="Mark as read"
                      >
                        <Check className="w-3.5 h-3.5 text-[#486581]" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      onClick={e => handleDelete(n.id, e)}
                      className="p-1 hover:bg-white rounded transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
                      aria-label="Delete notification"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
